"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import {
  Heart,
  Plus,
  Eye,
  Edit,
  Trash2,
  ExternalLink,
  LayoutDashboard,
  TrendingUp,
  Gift,
  QrCode,
  Copy,
  Check,
  CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";
import { LovePageCode } from "@/components/LovePageCode";
import { useDashboardStore, useWizardStore } from "@/store";
import { listGifts, deleteGift, createPlanCheckout } from "@/lib/api";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import { type Gift as GiftType, GiftStatus, Plan } from "@/types";
import { formatDate, giftStatusLabel, planLabel } from "@/lib/utils";

const STATUS_VARIANT: Record<GiftStatus, "success" | "warning" | "gray"> = {
  [GiftStatus.PUBLISHED]: "success",
  [GiftStatus.PENDING_PAYMENT]: "warning",
  [GiftStatus.DRAFT]: "gray",
};

const PLAN_VARIANT: Record<Plan, "default" | "gold" | "wine"> = {
  [Plan.BASIC]: "default",
  [Plan.PREMIUM]: "gold",
  [Plan.DELUXE]: "wine",
};

export default function DashboardPage() {
  const router = useRouter();
  const { user, isChecking } = useRequireAuth();
  const { gifts, isLoading, setGifts, setLoading, removeGift } = useDashboardStore();
  const { success, error: toastError } = useToast();
  const [codeGift, setCodeGift] = useState<GiftType | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [giftToDelete, setGiftToDelete] = useState<GiftType | null>(null);
  const [deleting, setDeleting] = useState(false);
  // ID do gift atualmente em "Pagar agora" — mantém o spinner só naquele card
  // sem bloquear ações nos outros (deletar, editar etc).
  const [payingGiftId, setPayingGiftId] = useState<string | null>(null);

  // Re-dispara o checkout no Mercado Pago para um gift que ficou em
  // PENDING_PAYMENT (preferência expirada, usuário fechou a aba do MP, etc).
  // O backend gera uma nova preferência e devolve a checkoutUrl, redirecionamos
  // direto via window.location.href (mesmo padrão do StepPlanCheckout).
  const handleRetryPayment = async (gift: GiftType) => {
    if (!gift.id || !gift.plan) {
      toastError(
        "Não foi possível retomar o pagamento",
        "Plano da ILovePage não disponível. Edite-a e tente de novo."
      );
      return;
    }
    setPayingGiftId(gift.id);
    const res = await createPlanCheckout({ giftId: gift.id, planId: gift.plan });
    if (!res.success) {
      setPayingGiftId(null);
      toastError(
        "Não foi possível retomar o pagamento",
        res.error.message ||
          "Tente novamente em instantes ou edite a ILovePage."
      );
      return;
    }
    const checkoutUrl = res.data.checkoutUrl;
    if (!checkoutUrl) {
      setPayingGiftId(null);
      toastError(
        "Não foi possível retomar o pagamento",
        "URL de pagamento não retornada pelo backend."
      );
      return;
    }
    window.location.href = checkoutUrl;
  };

  useEffect(() => {
    // Só busca gifts depois que sabemos quem é o user (evita 401 fantasma).
    if (isChecking || !user) return;
    setLoading(true);
    listGifts().then((res) => {
      if (res.success) setGifts(res.data.gifts);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChecking, user?.id]);

  const confirmDelete = async () => {
    if (!giftToDelete) return;
    setDeleting(true);
    const res = await deleteGift(giftToDelete.id);
    setDeleting(false);
    if (res.success) {
      removeGift(giftToDelete.id);
      success("ILovePage excluída", "A página foi removida com sucesso.");
      setGiftToDelete(null);
    } else {
      toastError("Erro ao excluir", "Tente novamente em instantes.");
    }
  };

  const copyCodeUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2500);
  };

  const published = gifts.filter((g) => g.status === GiftStatus.PUBLISHED).length;
  const drafts = gifts.filter((g) => g.status === GiftStatus.DRAFT).length;

  const codeShareUrl = codeGift
    ? `https://ilovepage.com.br/p/${codeGift.slug}`
    : "";

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#FFF8FA]">
      <div className="mx-auto max-w-5xl px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-[#E11D48] mb-1">
              <LayoutDashboard className="h-5 w-5" />
              <span className="text-xs font-bold uppercase tracking-widest">Painel</span>
            </div>
            <h1 className="text-3xl font-black text-[#881337]">Minhas ILovePages</h1>
            <p className="text-[#6B7280] text-sm mt-1">
              Gerencie todas as suas páginas românticas em um só lugar.
            </p>
          </div>
          <Button asChild>
            <Link
              href="/criar"
              onClick={() => useWizardStore.getState().reset()}
            >
              <Plus className="h-4 w-4" />
              Nova ILovePage
            </Link>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: "Total de páginas", value: gifts.length, icon: <Gift className="h-5 w-5 text-[#E11D48]" /> },
            { label: "Publicadas", value: published, icon: <Eye className="h-5 w-5 text-green-500" /> },
            { label: "Rascunhos", value: drafts, icon: <TrendingUp className="h-5 w-5 text-[#F59E0B]" /> },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className="p-5">
                <CardContent className="p-0 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
                    {stat.icon}
                  </div>
                  <div>
                    <p className="text-2xl font-black text-[#1F2937]">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Lista de gifts */}
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-white border border-rose-100 p-5 animate-pulse h-28" />
            ))}
          </div>
        ) : gifts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-20 text-center"
          >
            <div className="h-20 w-20 rounded-full bg-rose-50 flex items-center justify-center mb-4">
              <Heart className="h-10 w-10 text-rose-200" />
            </div>
            <h2 className="text-xl font-bold text-[#1F2937] mb-2">
              Nenhuma ILovePage ainda
            </h2>
            <p className="text-gray-500 text-sm max-w-sm mb-6">
              Crie sua primeira página romântica e surpreenda quem você ama!
            </p>
            <Button asChild>
              <Link href="/criar">
                <Plus className="h-4 w-4" />
                Criar minha primeira ILovePage
              </Link>
            </Button>
          </motion.div>
        ) : (
          <div className="flex flex-col gap-3">
            {gifts.map((gift, i) => {
              // Guard defensivo: a API normalmente devolve gifts bem-formados,
              // mas se em algum momento vier um null/undefined no array (cache
              // corrompido, race no setGifts, etc), evita o crash da página
              // inteira e só pula o item silenciosamente.
              if (!gift || !gift.id) return null;
              const photoCount = gift.photos?.length ?? 0;
              return (
              <motion.div
                key={gift.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
              >
                <Card className="hover:shadow-md transition-shadow duration-200">
                  <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-bold text-[#1F2937] truncate">{gift.title || "ILovePage"}</h3>
                          {gift.status && (
                            <Badge variant={STATUS_VARIANT[gift.status]}>
                              {giftStatusLabel(gift.status)}
                            </Badge>
                          )}
                          {gift.plan && (
                            <Badge variant={PLAN_VARIANT[gift.plan]}>
                              {planLabel(gift.plan)}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 truncate">
                          ilovepage.com.br/p/<span className="text-[#E11D48]">{gift.slug}</span>
                        </p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-400">
                          <span>Para: {gift.recipientName || "—"}</span>
                          <span>Criado em: {formatDate(gift.createdAt)}</span>
                          {photoCount > 0 && (
                            <span>{photoCount} foto{photoCount > 1 ? "s" : ""}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 flex-wrap justify-end -mr-1">
                        {gift.status === GiftStatus.PUBLISHED && (
                          <>
                            <Button variant="ghost" size="icon" asChild title="Ver ILovePage">
                              <Link href={`/p/${gift.slug}`} target="_blank">
                                <ExternalLink className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="ILovePage Code (QR)"
                              onClick={() => { setCodeCopied(false); setCodeGift(gift); }}
                              aria-label={`Abrir ILovePage Code de ${gift.title}`}
                            >
                              <QrCode className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {gift.status === GiftStatus.PENDING_PAYMENT && (
                          <Button
                            size="sm"
                            loading={payingGiftId === gift.id}
                            disabled={payingGiftId !== null}
                            onClick={() => handleRetryPayment(gift)}
                          >
                            <CreditCard className="h-4 w-4" />
                            Pagar agora
                          </Button>
                        )}
                        <Button variant="outline" size="sm" asChild>
                          <Link href={`/dashboard/${gift.id}/editar`}>
                            <Edit className="h-4 w-4" />
                            Editar
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setGiftToDelete(gift)}
                          className="text-gray-400 hover:text-red-500"
                          aria-label={`Excluir ${gift.title}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Modal ILovePage Code ─────────────────────────────────────────── */}
      <Dialog open={!!codeGift} onOpenChange={(open) => { if (!open) setCodeGift(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>ILovePage Code</DialogTitle>
            <DialogDescription>
              {codeGift?.title ?? "Compartilhe sua ILovePage"}
            </DialogDescription>
          </DialogHeader>

          {codeGift && (
            <div className="flex flex-col gap-4">
              <LovePageCode
                url={codeShareUrl}
                title={codeGift.title}
                coupleName={
                  codeGift.partnerName && codeGift.recipientName
                    ? `${codeGift.partnerName} & ${codeGift.recipientName}`
                    : codeGift.recipientName ?? undefined
                }
                templateSlug={codeGift.templateSlug ?? undefined}
                downloadable
                showQrCode
              />

              {/* Copiar URL com feedback */}
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => copyCodeUrl(codeShareUrl)}
                aria-label="Copiar link da ILovePage"
              >
                {codeCopied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {codeCopied ? "Link copiado!" : "Copiar link"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Modal de confirmação de exclusão ───────────────────────────── */}
      <Dialog
        open={!!giftToDelete}
        onOpenChange={(open) => { if (!open && !deleting) setGiftToDelete(null); }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="mx-auto h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mb-2">
              <Trash2 className="h-6 w-6 text-red-500" />
            </div>
            <DialogTitle className="text-center">Excluir esta ILovePage?</DialogTitle>
            <DialogDescription className="text-center">
              {giftToDelete ? (
                <>
                  Você está prestes a remover{" "}
                  <strong className="text-[#1F2937]">{giftToDelete.title}</strong>.
                  Essa ação não pode ser desfeita.
                </>
              ) : (
                "Essa ação não pode ser desfeita."
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2">
            <Button
              variant="ghost"
              onClick={() => setGiftToDelete(null)}
              disabled={deleting}
              className="text-gray-500"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              loading={deleting}
            >
              {deleting ? "Excluindo..." : "Sim, excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
